import {Injectable} from "@angular/core";
import {Observable} from "rxjs";
import {WordAudioPipelineService} from "./word-audio.pipeline.service";
import {GlossaryPipelineService} from "./glossary.pipeline.service";

import {ISelection,
    IExportGlossary,
    IGlossaryEntry,
    ITranslation,
    IText,
    ISentence,
    IWord,
    IQuestionEntry,
    IWordAudioTable,
    IAnswer} from "../../api/data-model";
import {UtilitiesService} from "../utilities.service";
import {HttpClient} from "@angular/common/http";
import {AudioState, Scilearn} from "../../constants";

export interface IWordAudioScript {
    [index: string]: Set<string>;

}

@Injectable()
export class ScriptPipelineService {
    private glossaryScript = "";
    private glossaryKeys = {};
    private glossaryEntries:IExportGlossary;
    private globalGlossaryEntries:IExportGlossary;
    private wordAudio:IWordAudioScript;
    // We used to specify .wav format in the audio scripts for the recording studio (want uncompressed for maximum flexibility)
    // with TTS, we do not currently do that (only mp3 available)
    private static WAV = ".wav";
    // The final compressed and uploaded form will be mp3 so the selection, glossary and word audio json files use that
    private static MP3 = ".mp3";

    constructor(public http:HttpClient, private wordAudioService:WordAudioPipelineService,
                private glossaryService:GlossaryPipelineService) {
        this.wordAudio = {};

    }

    public initScripts(): void {
        this.clearGlossary();
        this.wordAudio = {};
        this.glossaryKeys = {};

    }

    public exportScript(selection: ISelection): ISelection {
        let script = this.createSelectionScripts(selection);
        if (script) {
            script = '"TYPE","TEXT","FILENAME"\n' + script;
            UtilitiesService.downloadFile(script, selection.metaData.referenceStory + "-script.csv");
        }
        return selection;

    }
    public exportGlossaryScript(): void {
        // need to create scripts for the glossary, AND save entries with audio filenames back to glossary
        if (this.glossaryScript) {
            let glossaryScriptArray = this.glossaryScript.split("\n");
            let englishGlossaryScript = '"TYPE","TEXT","FILENAME"\n' + glossaryScriptArray
                .filter((line) => { return (line.indexOf('"TRANSLATION",') < 0); }).join("\n");
            let spanishGlossaryScript = '"TYPE","TEXT","FILENAME","TERM"\n' + glossaryScriptArray
                .filter((line) => { return (line.indexOf('"TRANSLATION",') >= 0); }).join("\n");
            UtilitiesService.downloadFile(englishGlossaryScript, "englishGlossary-script.csv");
            UtilitiesService.downloadFile(spanishGlossaryScript, "spanishGlossary-script.csv");
            // clear the script to avoid duplication if the scripting process is called again this session
            this.glossaryScript = "";
        }
    }

    public exportWordAudioScript(): void {
        // need to create scripts for word audio, AND save entries with audio filenames back to word audio table
        let script = this.getWordAudioScript();
        if (script) {
            script = '"TYPE","TEXT","FILENAME"\n' + script;
            UtilitiesService.downloadFile(script, "wordAudio-script.csv");
        }

    }

    public exportSharedData(type:string): void {
        if (type === "initial") {
            this.collectGlobalGlossary();
            this.glossaryScript = "";
        }
        if (type === "final") {
            // Only updating glossaryEntries, which is what is explicitly linked in the scripted selection(s)
            // Note: we may in some cases want to update globalGlossaryEntries, which is everything in the glossary that "needs" scripting
            Object.keys(this.glossaryEntries).forEach((id) => {
                this.glossaryService.putEntryById(this.glossaryEntries[id]);
            });
        }
    }

    public collectGlossary(glossaryEntries: IExportGlossary): void {
        let filteredIds: string[] =
            Object.keys(glossaryEntries).filter((s) => {
                return (!!glossaryEntries[s]);
            });
        filteredIds.forEach((id) => {
            let updatedEntry = this.scriptGlossaryEntry(glossaryEntries[id]);
            if (updatedEntry) {
                this.glossaryEntries[id] = updatedEntry;
            }
        });

    }

    private collectGlobalGlossary(): void {
        console.log("calling collect global glossary");
        Object.keys(this.glossaryService.glossary).forEach((key) => {
            this.glossaryService.glossary[key].entries.forEach((entry) => {
                if (!entry.id.match(/^[0-9]+$/)) {
                    let updatedEntry = this.scriptGlossaryEntry(entry);
                    if (updatedEntry) {
                        this.globalGlossaryEntries[updatedEntry.id] = updatedEntry;
                    }
                }
            });
        });
    }

    public updateTranslation(glossaryEntries: IExportGlossary, selection: ISelection): void {
        selection.text.forEach((text:IText) => {
            if (text.sentences) {
                text.sentences.forEach((sentence: ISentence) => {
                    sentence.words.forEach((word: IWord) => {
                        let language = "es";
                        if (word.glossary && word.translations && word.translations[language] &&
                            word.translations[language].startsWith("NEEDSTRANSLATION")) {
                            if (glossaryEntries[word.glossary] && glossaryEntries[word.glossary].translations.length) {
                                let filteredTranslations = glossaryEntries[word.glossary].translations.filter((t) => {
                                    return (t.language === language);
                                });
                                console.log(JSON.stringify(filteredTranslations));
                                let translationId = filteredTranslations[0].id;
                                word.translations[language] = translationId;
                                if (filteredTranslations.length > 1) {
                                    console.log("warning: multiple translations found for " + word.glossary);
                                }
                            } else {
                                console.log("error: no glossary or no translation for " + word.glossary);
                            }
                        }
                    });
                });
            }

        });
    }

    private scriptGlossaryEntry(entry: IGlossaryEntry): IGlossaryEntry {
        let newEntry: IGlossaryEntry;
        let script = "";
        let textScript = "";
        let fileName:string;
        newEntry = JSON.parse(JSON.stringify(entry));
        Object.keys(newEntry).forEach((property) => {
            switch (property) {
                case "syllables":
                case "definition":
                case "fact":
                case "context":
                    if (newEntry[property].text && /*!newEntry[property].audio &&*/ this.shouldScript(newEntry[property].audioState)) {
                        let typeId = "-" + property;
                        if (property === "syllables") {
                            typeId = "";
                        }
                        if (property === "definition" && newEntry[property].text.endsWith(".")) {
                            console.log("removing period from definition:" + newEntry[property].text);
                            newEntry[property].text = newEntry[property].text.replace(/[.]\s*$/, "");
                            console.log("after removing period from definition:" + newEntry[property].text);
                        }
                        let baseName = UtilitiesService.convertNonASCIIToASCII(newEntry.id).replace(/[/,]/g, "_");
                        fileName = baseName + typeId;
                        textScript = this.quoteTextForCSV(newEntry[property].text.replace("\n", " "));
                        // Using empty typeId above to refer to syllables
                        if (typeId == "") {
                            textScript = textScript.replace(/\^|\./g, "");
                        }
                        script += '"GLOSSARY",' + textScript + ',"' + fileName + ScriptPipelineService.MP3 + '"\n';
                        newEntry[property].audio = fileName + ScriptPipelineService.MP3;
                        newEntry[property].audioState = AudioState.SCRIPTED;
                    }
                    break;
                case "translations":
                    let translation:ITranslation = newEntry[property].find((t:ITranslation) => {
                        return (t.language === "es");
                    });
                    if (translation && translation.word && /*!translation.audio &&*/ this.shouldScript(translation.audioState)) {
                        if (!translation.id) {
                            let index = newEntry.translations.indexOf(translation) + 1;
                            translation.id =
                                translation.word.toLowerCase().split(/\s+/).join("_") + "-" + index.toString();
                        }
                        textScript = this.quoteTextForCSV(translation.word);
                        let word = this.quoteTextForCSV(newEntry.word);
                        // S3 does not deal well with accented characters in spanish translation audio filenames.
                        // to avoid audio file name clashes, need to add the -es (if not already there)
                        // finally punctuation is sometimes used, but we don't want in name
                        let fileTranslationId = translation.id;
                        if (!fileTranslationId.endsWith("-" + translation.language)) {
                            fileTranslationId += "-" + translation.language;
                        }
                        fileName =
                            UtilitiesService.convertNonASCIIToASCII(fileTranslationId).replace(/[?:!.\]\[)(/,;]/g, "_");
                        script += '"TRANSLATION",' + textScript + ',"' +
                                  fileName + ScriptPipelineService.MP3 + '",' + word + "\n";
                        translation.audio = fileName + ScriptPipelineService.MP3;
                        translation.audioState = AudioState.SCRIPTED;
                    }
                    break;
                default:
                    break;
            }
        });
        this.glossaryScript += script;
        if (script) {
            this.glossaryKeys[newEntry.id] = script;
            return newEntry;
        }
        return null;

    }

    private needsGlossaryScript(entry:IGlossaryEntry): boolean {
        let needsScript = !this.glossaryEntries[entry.id];
        return needsScript;
    }

    public clearGlossary(): void {
        this.glossaryEntries = {};
        this.globalGlossaryEntries = {};
    }

    public createSelectionScripts(selection:ISelection):string {
        let selectionScript = "";
        if (selection.metaData.introText && this.shouldScript(selection.metaData.audioState)) {
            let introScript = this.quoteTextForCSV(selection.metaData.introText.trim());
            let fileName = selection.metaData.referenceStory + "-intro";
            selectionScript += '"INTRO",' + introScript + ',"' + fileName + ScriptPipelineService.MP3 + '"\n';
            selection.metaData.introAudio = fileName + ScriptPipelineService.MP3;
            selection.metaData.audioState = AudioState.SCRIPTED;
        }
        selection.text.forEach((text:IText) => {
            if (text.sentences) {
                text.sentences.forEach((sentence: ISentence) => {
                    if (this.shouldScript(sentence.audioState)) {
                        let script = this.getSentenceScript(sentence);
                        let fileName = selection.metaData.referenceStory + "-s-" + sentence.id;
                        selectionScript += '"SENTENCE",' + script + ',"' + fileName + ScriptPipelineService.MP3 + '"\n';
                        sentence.audio = fileName + ScriptPipelineService.MP3;
                        sentence.audioState = AudioState.SCRIPTED;
                        this.getSentenceWordAudio(sentence, selection.metaData.referenceStory);
                    }
                });
            }

        });
        let questionScript:string = this.getQuestionScript(selection);
        return selectionScript + questionScript;
    }

    public getQuestionScript(selection:ISelection):string {

        let questionAudioScript = "";
        let indexTAI = 1;
        let indexQuiz = 1;
        let indexString = "";
        let type = "";
        selection.questionEntries.forEach((question:IQuestionEntry) => {
            if (question.presentation === Scilearn.THINK_ABOUT_IT) {
                indexString = indexTAI.toString(10);
                indexTAI++;
                type = "t";
            } else {
                indexString = indexQuiz.toString(10);
                indexQuiz++;
                type = "q";
            }
            let filename = selection.metaData.referenceStory + "-" + type + "-" + indexString;
            if (!question.question.audioState || this.shouldScript(question.question.audioState)) {
                console.log("scripting question");
                questionAudioScript += '"QUESTION",' + this.quoteTextForCSV(question.question.text.trim()) + ',"' +
                    filename + ScriptPipelineService.MP3 + '"\n';
                question.question.audio = filename + ScriptPipelineService.MP3;
                question.question.audioState = AudioState.SCRIPTED;
            }
            if (question.type !== "OpenBook") {
                console.log("scripting answers, not open book");
                question.answers.forEach((answer: IAnswer, index) => {
                    let answerIndex = index + 1;
                    if (!answer.audioState || this.shouldScript(answer.audioState)) {
                        console.log("scripting answers, audiostate");
                        let answerFilename = filename + "-a-" + answerIndex.toString(10);
                        questionAudioScript += '"QUESTION",' + this.quoteTextForCSV(answer.text) + ',"' +
                            answerFilename + ScriptPipelineService.MP3 + '"\n';
                        answer.audio = answerFilename + ScriptPipelineService.MP3;
                        answer.audioState = AudioState.SCRIPTED;
                    }

                });
            }

        });

        return questionAudioScript;
    }
    public getWordAudioScript(): string {

        let wordAudioScript = "";
        for (let word in this.wordAudio) {
            if (this.wordAudio.hasOwnProperty(word)) {
                let filename = this.getFilenameFromWord(word) + ScriptPipelineService.MP3;
                let selectionList = "";
                this.wordAudio[word].forEach((selection) => {
                    selectionList += selection + " ";
                });
                wordAudioScript +=
                    UtilitiesService.convertPunctuationToASCII('"WORD",' + this.quoteTextForCSV(word) +
                                                               ',"' + filename + '","' + selectionList + '"\n');
            }

        }

        return wordAudioScript;

    }

    public clearWordAudio(): void {
        this.wordAudio = {};
    }

    private getFilenameFromWord(wordString:string):string {
        wordString = UtilitiesService.convertNonASCIIToASCII(UtilitiesService.convertPunctuationToASCII(wordString));
        // prefix numeric items to avoid name clashes with legacy audio
        if (wordString.match(/^\d/)) {
            wordString = "word_audio_" + wordString;
        }
        return wordString.split(" ").join("_");
    }

    public getSentenceScript(sentence: ISentence): string {
        let sentenceScript = "";
        sentence.words.forEach((word: IWord) => {
            let wordScript = word.prefix + word.word + word.separator;
            wordScript = wordScript.trim() + " ";
            sentenceScript += wordScript;
        });
        return this.quoteTextForCSV(sentenceScript);

    }

    private quoteTextForCSV(text: string): string {
        // convert any non-ASCII punctuation to ASCII to avoid spreadsheet import issues
        // double any internal " characters to escape them, then enclose entire string in "
        text = UtilitiesService.convertPunctuationToASCII(text);
        text = text.replace(/\"/g, '""');
        text = '"' + text + '"';
        return text;
    }

    private getSentenceWordAudio(sentence: ISentence, referenceStory: string): void {

        sentence.words.forEach((word: IWord) => {
            let wordScript: string = this.wordAudioService.getWordKey(word);
            if (!this.wordAudioService.getWord(word)) {
                // !1 and !2 notations are for dealing with different pronunciations of a word with the same spelling
                // e.g. read = "reed" (present tense) vs. read = "red" (past tense)
                if (this.wordAudioService.getAudioForWord(wordScript + "!1")?.length) {
                    wordScript += "!1";
;               }
                else if (this.wordAudioService.getAudioForWord(wordScript + "!2")?.length) {
                    wordScript += "!2";
                }
            }
            if (this.wordAudio[wordScript]) {
                this.wordAudio[wordScript].add(referenceStory);
            }
            if (!this.wordAudioService.getAudioForWord(wordScript)?.length ||
                this.shouldScript(this.wordAudioService.getAudioStateForWord(wordScript))) {
                // add to script, add filename
                if (this.wordAudio[wordScript]) {
                    this.wordAudio[wordScript].add(referenceStory);
                } else {
                    this.wordAudio[wordScript] = new Set([referenceStory]);
                }
                if (!word.audio) {
                    word.audio = this.getFilenameFromWord(wordScript) + ScriptPipelineService.MP3;
                }
                console.log("adding missing word to audio table: " + wordScript);
                this.wordAudioService.addWordToAudioTable(word, true);
            } else {
                // TODO: actually a lot of words have more than one file - should review, not just take the first?
                // if already scripted an audio entry, don't overwrite
                if (!word.audio) {
                    word.audio = this.wordAudioService.getAudioForWord(wordScript)[0];
                }

            }

        });
    }

    private shouldScript(state: string) {
        if (state === AudioState.SCRIPTED || state === AudioState.UNRECORDED
            || state === AudioState.MISSING || state === AudioState.RERECORD
        ) {
            return true;
        }
        return false;
    }

}
